import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, H1, Li, P, PRINT_URL, Span, URL, useIovation, formatPhoneNumber, AnalyticsContext, } from 'shared';
import { usePostConsumerApplicationsV2ApproveChargeFlowsApproveChargeFlowIdAcceptMutation, } from 'consumer-applications-v2-api';
import checkIcon from 'images/utility-icons/check-green.svg';
import dropdownIcon from 'images/utility-icons/dropdown.svg';
import printIcon from 'images/utility-icons/print-icon-red.svg';
import { setApproveChargeFlowData } from '../../actions';
import { selectors } from '../../reducer';
import { createStatusRouter } from '../utils';
function TermsAndSubmission() {
    const history = useHistory();
    const dispatch = useDispatch();
    const { path } = useRouteMatch();
    const [getBlackboxData] = useIovation();
    const { analytics } = useContext(AnalyticsContext);
    // Clean the path by removing /terms-submission for routing
    const parentPath = path.replace('/terms-submission', '');
    const handleStatusRouting = createStatusRouter(history, dispatch, analytics, parentPath);
    const approveChargeFlowData = useSelector(selectors.approveChargeFlowData);
    const checklistData = useMemo(() => [
        {
            title: 'Review and Submit',
            content: [
                'Please review the disclosures presented in the Electronic Consent section of the Terms and Conditions, Credit Card Agreement, Privacy Statement, and other information presented in the Terms and Conditions prior to submitting and retain a copy for your records.',
                'By submitting this application/solicitation, each applicant ("I", "me" or "my" below) agrees and certifies that (1) I have read and agree to the disclosures provided on or with this application/solicitation, (2) the information I have supplied is true and correct, (3) I am applying to Comenity Capital Bank, PO Box 183003 Columbus, OH 43218-3003 ("Bank") for an Alphaeon Credit Card Account (4) I authorize the Bank to obtain credit reports on me, (5) if approved, my account will be governed by the Credit Card Agreement. (6) I understand that I may pay all of my account balance at any time without penalty and (7) this application/solicitation, any information submitted to the Bank, and the Bank\'s final decision on my application/solicitation may be shared with and retained by Alphaeon.',
            ],
        },
        {
            title: 'E-Sign and Consent to Electronic Communication',
            content: '',
            url: URL.CCA,
            iframeSrc: URL.CCA,
            iframeTitle: 'Electronic Communication Consent',
        },
        {
            title: 'Consent to Account Terms and Conditions',
            content: '',
            url: URL.PATRIOT_ACT,
            iframeSrc: URL.PATRIOT_ACT,
            iframeTitle: 'Account Terms and Conditions',
        },
        {
            title: 'Wireless Account Information',
            content: `By proceeding, you may receive a one-time security link to your mobile number: ${formatPhoneNumber(approveChargeFlowData?.phoneNumber)}. Message and data rates apply. You authorize your wireless carrier to use or disclose information about your wireless account and your wireless device, if available, to Comenity Capital Bank or its service provider for the duration of your business relationship, solely to help them identify you and your wireless device and to prevent fraud.`,
        },
        {
            title: 'Hard Credit Inquiry',
            content: 'I understand that by clicking "SUBMIT", I am applying for credit with Comenity Capital Bank, agreeing to the Electronic Consent section of the Terms and Conditions, the Credit Card Agreement, Financial Privacy Policy, Online and California Privacy Policy, and consenting to electronically receiving documents and am electronically signing this application/solicitation.<br/><br/>• <a href="https://d.comenity.net/legaldocs/alphaeoncosmetic/legal-documents/esign-agreement" target="_blank" rel="noopener noreferrer">Electronic Consent</a><br/>• <a href="https://www.breadfinancial.com/en/privacy.html" target="_blank" rel="noopener noreferrer">Financial Privacy Policy</a><br/>• <a href="https://www.breadfinancial.com/en/privacy.html" target="_blank" rel="noopener noreferrer">Online and California Privacy Policy</a>',
        },
    ], [approveChargeFlowData?.phoneNumber]);
    const [openIndex, setOpenIndex] = useState(0);
    const [acknowledged, setAcknowledged] = useState(Array(checklistData.length).fill(false));
    const [expandedAfterAck, setExpandedAfterAck] = useState(null);
    const [acceptApproveChargeFlow, { isLoading: isSubmitting }] = usePostConsumerApplicationsV2ApproveChargeFlowsApproveChargeFlowIdAcceptMutation();
    const handleDropdownClick = (idx) => {
        setOpenIndex(idx);
    };
    const handleExpandAfterAck = (idx) => {
        setExpandedAfterAck(expandedAfterAck === idx ? null : idx);
    };
    const handleAcknowledge = (idx) => {
        setAcknowledged((prev) => {
            const next = [...prev];
            next[idx] = true;
            return next;
        });
        if (idx < checklistData.length - 1) {
            setOpenIndex(idx + 1);
        }
        else {
            setOpenIndex(null);
        }
    };
    // Map external URLs to internal print routes
    const getPrintRoute = (url) => {
        if (url === URL.CCA) {
            return `${PRINT_URL}/CCA`;
        }
        if (url === URL.PATRIOT_ACT) {
            return `${PRINT_URL}/PATRIOT_ACT`;
        }
        return null;
    };
    const handlePrint = (idx) => {
        const item = checklistData[idx];
        // Only handle external links
        if (item.url) {
            const printRoute = getPrintRoute(item.url);
            if (printRoute) {
                window.open(printRoute, '_blank');
            }
        }
    };
    // Check if all previous items are acknowledged
    const canExpandItem = (idx) => {
        if (idx === 0)
            return true; // First item is always expandable
        return acknowledged.slice(0, idx).every((item) => item);
    };
    const handleSubmitSuccess = (result) => {
        dispatch(setApproveChargeFlowData(result));
        handleStatusRouting(result);
    };
    const handleSubmit = async () => {
        if (!approveChargeFlowData?.id ||
            !approveChargeFlowData?.offers?.[0]?.offerId) {
            // No approve charge flow ID available
            return;
        }
        const blackboxData = getBlackboxData();
        const result = await acceptApproveChargeFlow({
            approveChargeFlowId: approveChargeFlowData.id,
            postConsumerApplicationsV2ApproveChargeFlowsApproveChargeFlowIdAccept: {
                acceptedOfferId: approveChargeFlowData.offers[0].offerId, // Hardcoded value
                termsLanguage: 'en', // Hardcoded value
                prerequisiteTermsAccepted: [
                    {
                        termContentId: 1, // Hardcoded value
                        acceptedOn: new Date().toISOString(), // Current timestamp
                    },
                ],
                iovationBlackBox: blackboxData.blackbox,
            },
        }).unwrap();
        // Approve charge flow accepted: result
        // Set lender name in Redux from the result
        if (result) {
            handleSubmitSuccess(result);
        }
    };
    return (_jsxs("div", { className: "terms-and-submission-container", children: [_jsx(H1, { className: "mar-t-40 mar-b-20 text-left", variant: "headline-large", children: "Application Submission" }), _jsxs("div", { className: "terms-and-submission-card w100 desktop-card approve-charge", children: [_jsx("ol", { className: "mar-b-10", children: checklistData.map((item, idx) => (_jsxs(Li, { variant: "body-large", listStyle: "disc", className: "mar-b-10", children: [_jsxs("div", { className: "dropdown-container", children: [acknowledged[idx] && (_jsx(Button, { type: "button", className: "empty-button mar-r-4", onClick: () => handleExpandAfterAck(idx), children: _jsx("img", { src: dropdownIcon, alt: "Expand", className: `dropdown-icon ${expandedAfterAck === idx
                                                    ? 'dropdown-icon--expanded'
                                                    : ''}` }) })), _jsx(Button, { type: "button", style: {
                                                cursor: canExpandItem(idx) && !acknowledged[idx]
                                                    ? 'pointer'
                                                    : 'default',
                                            }, className: "empty-button", onClick: () => {
                                                if (canExpandItem(idx) && !acknowledged[idx]) {
                                                    handleDropdownClick(idx);
                                                }
                                            }, disabled: acknowledged[idx] || !canExpandItem(idx), children: _jsx("img", { src: acknowledged[idx] ? checkIcon : dropdownIcon, alt: acknowledged[idx] ? 'Checked' : 'Dropdown', className: `${acknowledged[idx] ? 'check-icon' : 'dropdown-icon'} ${openIndex === idx && !acknowledged[idx]
                                                    ? 'dropdown-icon--expanded'
                                                    : ''}` }) }), _jsx(P, { variant: "body-large", className: openIndex === idx && !acknowledged[idx]
                                                ? 'text-red text-bold'
                                                : '', children: item.title }), acknowledged[idx] && item.url && (_jsx(Button, { type: "button", className: "empty-button", style: { cursor: 'pointer' }, onClick: () => handlePrint(idx), children: _jsx("img", { src: printIcon, alt: "Print", className: "print-icon" }) }))] }), ((openIndex === idx && !acknowledged[idx]) ||
                                    (expandedAfterAck === idx && acknowledged[idx])) && (_jsxs(_Fragment, { children: [item.iframeSrc ? (_jsx("div", { className: "iframe-content", children: _jsx("iframe", { id: `legal-doc-${idx}`, src: item.iframeSrc, title: item.iframeTitle }) })) : (_jsx("div", { className: "dropdown-content", children: Array.isArray(item.content) ? (item.content.map((paragraph, pIdx) => (_jsx(P, { className: "body-medium", children: paragraph }, pIdx)))) : (_jsx(P, { className: "body-medium", dangerouslySetInnerHTML: { __html: item.content } })) })), !acknowledged[idx] && (_jsx("div", { className: "mar-t-16", children: _jsx(Button, { type: "button", className: "button-primary button-warn w100", onClick: idx === checklistData.length - 1
                                                    ? () => handleSubmit()
                                                    : () => handleAcknowledge(idx), submitting: isSubmitting, children: idx === checklistData.length - 1
                                                    ? 'Submit'
                                                    : 'I understand' }) }))] }))] }, item.title))) }), _jsx("div", { className: "w100 flex flex-center mar-y-20", children: _jsx("div", { className: "or-divider", children: _jsx(Span, { variant: "body-medium", children: "or" }) }) }), _jsx("div", { className: "w100 flex flex-center", children: _jsx("button", { type: "button", className: "go-back-link", onClick: () => history.goBack(), children: "Go back" }) })] })] }));
}
export default TermsAndSubmission;
